import React, { useState, useRef, useEffect, useMemo, useCallback, useDeferredValue } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useAutomationsSync } from "../../../hooks/useAutomationsSync";
import {
  AutomationsHeader,
  TimelineListitem,
  LiveDot,
  ScheduleModal,
  AutomationEditModal,
  AutomationCreateModal,
  RunHistoryModal,
  RunDetailsModal,
  ImageGalleryModal,
  DeleteConfirmModal,
  EmptyState,
  FilterPanel,
} from "./components";
import { DEFAULT_FILTERS } from "./components/FilterPanel";
import ResumeConfirmModal from "./components/ResumeConfirmModal";
import NoCreditsModal from "../../../components/NoCreditsModal";
import MixedDomainsModal from "../../../components/MixedDomainsModal";
import LowCreditsBanner from "../../../components/LowCreditsBanner";
import QueuedAutomationsBanner from "../../../components/QueuedAutomationsBanner";
import { formatLastRun } from "./automationUtils";

/**
 * AutomationsView Component
 *
 * Displays a list of automations with timeline visualization and controls.
 * All sync logic (polling, optimistic updates) is handled by useAutomationsSync hook.
 */
const AutomationsView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace, subscription, credits } = useAuth();

  // === UI Preferences (persisted) ===
  const [timeRange, setTimeRange] = useState(() =>
    localStorage.getItem('automations-timeline-view') || 'weekly'
  );
  const [scheduledOnly, setScheduledOnly] = useState(() =>
    localStorage.getItem('automations-scheduled-filter') === 'true'
  );
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('automations-filters');
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : { ...DEFAULT_FILTERS };
    } catch { return { ...DEFAULT_FILTERS }; }
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterButtonRef = useRef(null);

  // Persist filters to localStorage
  const handleFiltersChange = useCallback((next) => {
    setFilters(next);
    localStorage.setItem('automations-filters', JSON.stringify(next));
  }, []);

  // Count of active (non-default) filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status?.length > 0) count++;
    if (filters.taskType) count++;
    if (filters.sortBy !== 'created_at') count++;
    if (filters.sortOrder !== 'desc') count++;
    return count;
  }, [filters]);

  const handleClearFilters = useCallback(() => {
    handleFiltersChange({ ...DEFAULT_FILTERS });
    if (scheduledOnly) handleScheduledOnlyChange(false);
  }, [handleFiltersChange, scheduledOnly]);

  // === Sync Hook - All API/polling logic encapsulated ===
  const {
    automations,
    loading,
    filtering,
    summary,
    totalTasks,
    hasMore,
    loadingMore,
    loadMore,
    liveStats,
    scheduledAutomations,
    upcomingScheduled,
    runAutomation,
    stopAutomation,
    resumeAutomation,
    updateSchedule,
    deleteAutomation,
    deleteAutomationsBulk,
    refresh,
    runningIds,
    stoppingIds,
    resumingIds,
  } = useAutomationsSync({
    workspaceId: currentWorkspace?.id,
    scheduledOnly,
    filters,
    limit: 10,
  });

  // === Search State (debounced inside SearchInput for smooth typing) ===
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const isSearchStale = searchQuery !== deferredSearchQuery;

  const filteredAutomations = useMemo(() => {
    if (!deferredSearchQuery.trim()) return automations;
    const q = deferredSearchQuery.toLowerCase().trim();
    return automations.filter(a =>
      a.name?.toLowerCase().includes(q) ||
      a.website?.toLowerCase().includes(q)
    );
  }, [automations, deferredSearchQuery]);

  // === List Animation: track seen IDs to avoid re-animating on polls ===
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    seenIdsRef.current.clear();
  }, [deferredSearchQuery, filters, scheduledOnly]);

  const getItemAnimationStyle = useCallback((automationId, index) => {
    if (seenIdsRef.current.has(automationId)) return undefined;
    seenIdsRef.current.add(automationId);
    const cappedIndex = Math.min(index, 9);
    return {
      animation: 'listItemIn 0.3s ease-out both',
      animationDelay: `${cappedIndex * 50}ms`,
    };
  }, []);

  // === Modal State ===
  const [scheduleModal, setScheduleModal] = useState(null);
  const [settingsModal, setSettingsModal] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [resumeModal, setResumeModal] = useState(null); // { automation, failedRunId }
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [showMixedDomainsModal, setShowMixedDomainsModal] = useState(false);

  // The open run dialog lives in the URL (?automation=<taskId>&run=<runId>) rather than in state:
  // a run is then shareable and closable with the back button, and the link MCP agents hand out
  // (`live_progress`) is the same one the UI produces when you open a run by hand.
  const openRun = useCallback(
    (automation, runId, { replace = false } = {}) =>
      setSearchParams({ automation: automation.id, run: runId }, { replace }),
    [setSearchParams]
  );
  const closeRun = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

  const runModal = useMemo(() => {
    const runId = searchParams.get("run");
    const automationId = searchParams.get("automation");
    if (!runId || !automationId) return null;
    const automation = automations.find((a) => a.id === automationId);
    return automation ? { automation, runId } : null;
  }, [searchParams, automations]);

  // === Credits & Plan State (for banners) ===
  const planName = subscription?.subscription?.plan?.name || 'free';
  const [lowCreditsDismissed, setLowCreditsDismissed] = useState(
    () => sessionStorage.getItem('lowCreditsBannerDismissed') === 'true'
  );

  const shouldShowLowCreditsBanner = credits && credits.total > 0 && (
    credits.available === 0 ||
    (credits.available / credits.total < 0.2 && !lowCreditsDismissed)
  );

  // === Queued automations banner (browser limit upsell) ===
  const [queuedBannerDismissed, setQueuedBannerDismissed] = useState(() => {
    const dismissedAt = localStorage.getItem('queuedBannerDismissedAt');
    if (!dismissedAt) return false;
    const oneDayMs = 24 * 60 * 60 * 1000;
    return Date.now() - Number(dismissedAt) < oneDayMs;
  });
  const runningCount = summary?.running_count || 0;
  const queuedCount = summary?.queued_count || 0;
  const isQueuedBannerEligiblePlan = planName === 'free' || planName === 'starter';
  const shouldShowQueuedBanner = runningCount > 0 && queuedCount > 0 && isQueuedBannerEligiblePlan && !queuedBannerDismissed;

  // === Selection State (for bulk operations) ===
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Ref for schedule button positioning
  const scheduleButtonRefs = useRef({});

  // === UI Handlers ===
  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    localStorage.setItem('automations-timeline-view', newRange);
  };

  const handleScheduledOnlyChange = (value) => {
    setScheduledOnly(value);
    localStorage.setItem('automations-scheduled-filter', value.toString());
  };

  const handleRunNow = async (automationId) => {
    try {
      await runAutomation(automationId);
    } catch (error) {
      if (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PLAN_REQUIRED') {
        setShowNoCreditsModal(true);
      } else {
        alert(error.message || 'Failed to run automation');
      }
    }
  };

  const handleStopAutomation = async (automation) => {
    try {
      await stopAutomation(automation);
    } catch (error) {
      alert(error.message || 'Failed to stop automation');
    }
  };

  const handleOpenResumeModal = (automation, latestRun) => {
    setResumeModal({ automation, failedRunId: latestRun.runId });
  };

  const handleConfirmResume = async () => {
    if (!resumeModal) return;
    try {
      await resumeAutomation(resumeModal.automation, resumeModal.failedRunId);
    } catch (error) {
      if (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PLAN_REQUIRED') {
        setResumeModal(null);
        setShowNoCreditsModal(true);
        return;
      }
      alert(error.message || 'Failed to resume automation');
    }
    setResumeModal(null);
  };

  const handleSettings = (automation, initialCategory) => {
    setSettingsModal({ automation, initialCategory });
  };

  const handleSaveSettings = async () => {
    // Settings modal handles its own API call - just refresh after save
    refresh();
  };

  const handleViewData = (automation) => {
    if (automation.latestTableId) {
      window.open(`/table/${automation.latestTableId}`, '_blank');
    }
  };

  const handleViewHistory = (automation) => {
    setHistoryModal(automation);
  };

  const handleViewRunDetails = (automation) => {
    const latestRun = automation.timeline?.[0];
    if (latestRun?.runId) {
      openRun(automation, latestRun.runId);
    }
  };

  const handleViewRunDetailsFromHistory = (runId) => {
    if (!historyModal) return;
    openRun(historyModal, runId);
  };

  const handleViewRunDataFromHistory = (tableId) => {
    if (tableId) {
      navigate(`/table/${tableId}`);
    }
  };

  const handleSaveSchedule = async (automationId, schedule) => {
    try {
      await updateSchedule(automationId, schedule);
    } catch (error) {
      alert(error.message || 'Failed to update schedule');
    }
  };

  const handleTimelineClick = (automation, point) => {
    if (point.status === "empty" || !point.runId) return;
    openRun(automation, point.runId);
  };

  // === Infinite Scroll ===
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    const container = scrollContainerRef.current;
    if (!trigger || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  // === Skeleton: show instantly, minimum 2s visible, then hide ===
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  const skeletonShowTimeRef = useRef(0);
  const skeletonTimerRef = useRef(null);

  useEffect(() => {
    if (loadingMore) {
      clearTimeout(skeletonTimerRef.current);
      skeletonShowTimeRef.current = Date.now();
      setSkeletonVisible(true);
    } else if (skeletonVisible) {
      const elapsed = Date.now() - skeletonShowTimeRef.current;
      const remaining = Math.max(0, 2000 - elapsed);
      skeletonTimerRef.current = setTimeout(() => setSkeletonVisible(false), remaining);
    }
    return () => clearTimeout(skeletonTimerRef.current);
  }, [loadingMore]);

  // === Selection Handlers ===
  const handleToggleSelect = (automationId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(automationId)) {
        next.delete(automationId);
      } else {
        next.add(automationId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredAutomations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAutomations.map(a => a.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  // === Helper: Check if automation is running/queued ===
  const isAutomationRunning = (automation) => {
    return automation?.timeline?.some(t => t.status === 'running' || t.status === 'queued');
  };

  // === Computed: Count of selected running automations ===
  const selectedRunningCount = [...selectedIds].filter(id => {
    const automation = automations.find(a => a.id === id);
    return isAutomationRunning(automation);
  }).length;

  // === Delete Handlers ===
  const handleDeleteAutomation = async (automationId) => {
    try {
      await deleteAutomation(automationId);
      // Remove from selection if selected
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(automationId);
        return next;
      });
    } catch (error) {
      // Handle 429 (rate limit / cannot delete running)
      if (error.status === 429 || error.message?.includes('running')) {
        alert('Cannot delete automation while it is running. Please stop it first.');
      } else {
        alert(error.message || 'Failed to delete automation');
      }
      throw error;
    }
  };

  const handleBulkDelete = async () => {
    // Filter out running automations - only delete the ones that can be deleted
    const deletableIds = [...selectedIds].filter(id => {
      const automation = automations.find(a => a.id === id);
      return !isAutomationRunning(automation);
    });

    if (deletableIds.length === 0) {
      setShowBulkDeleteModal(false);
      return;
    }

    try {
      await deleteAutomationsBulk(deletableIds);
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
    } catch (error) {
      // Handle 429 (rate limit / cannot delete running)
      if (error.status === 429 || error.message?.includes('running')) {
        alert('Some automations could not be deleted because they are currently running.');
      } else {
        alert(error.message || 'Failed to delete automations');
      }
    }
  };

  // === Loading State ===
  if (loading && automations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 dark:border-gray-700 border-t-blue-500"></div>
        </div>
        <p className="mt-4 text-xs text-gray-600 dark:text-gray-500 font-medium">Loading automations...</p>
      </div>
    );
  }

  // === Main Render ===
  return (
    <div className="flex flex-col h-full w-full overflow-hidden gap-3">
      {/* Low credits banner */}
      {shouldShowLowCreditsBanner && (
        <LowCreditsBanner
          credits={credits}
          isFreePlan={!planName || planName === 'free'}
          onDismiss={() => {
            setLowCreditsDismissed(true);
            sessionStorage.setItem('lowCreditsBannerDismissed', 'true');
          }}
          onUpgrade={() => navigate('/plans')}
          onTopUp={() => navigate('/topups')}
        />
      )}

      {/* Queued automations banner — browser limit upsell */}
      {shouldShowQueuedBanner && !shouldShowLowCreditsBanner && (
        <QueuedAutomationsBanner
          queuedCount={queuedCount}
          onDismiss={() => {
            setQueuedBannerDismissed(true);
            localStorage.setItem('queuedBannerDismissedAt', String(Date.now()));
          }}
          onUpgrade={() => navigate('/plans')}
        />
      )}

      {/* Top Navbar - shown when automations exist OR filters are active (so user can clear them) */}
      {(automations.length > 0 || activeFilterCount > 0) && (
        <>
          <AutomationsHeader
            automationsCount={automations.length}
            summary={summary}
            upcomingScheduled={upcomingScheduled}
            timeRange={timeRange}
            onTimeRangeChange={handleTimeRangeChange}
            scheduledOnly={scheduledOnly}
            onScheduledOnlyChange={handleScheduledOnlyChange}
            liveStats={liveStats}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onCreateNew={() => setCreateModal(true)}
            activeFilterCount={activeFilterCount}
            filterButtonRef={filterButtonRef}
            onToggleFilters={() => setShowFilterPanel(prev => !prev)}
          />
          {showFilterPanel && (
            <FilterPanel
              filters={filters}
              onChange={handleFiltersChange}
              onClose={() => setShowFilterPanel(false)}
              anchorRef={filterButtonRef}
            />
          )}
        </>
      )}

      {/* Content area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar">
       <div className={`transition-opacity duration-200 ${(isSearchStale || filtering) ? 'opacity-60' : 'opacity-100'}`}>
        {automations.length === 0 && (activeFilterCount > 0 || scheduledOnly) ? (
          <EmptyState
            onCreateNew={() => setCreateModal(true)}
            hasActiveFilters={true}
            onClearFilters={handleClearFilters}
            filters={filters}
          />
        ) : automations.length === 0 ? (
          <EmptyState onCreateNew={() => setCreateModal(true)} />
        ) : filteredAutomations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
            <div className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">No matching automations</div>
            <div className="text-gray-600 dark:text-gray-600 text-xs">Try adjusting your search term</div>
          </div>
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden lg:block space-y-3 lg:space-y-3">
              {filteredAutomations.map((automation, index) => {
                if (!scheduleButtonRefs.current[automation.id]) {
                  scheduleButtonRefs.current[automation.id] = React.createRef();
                }
                const animStyle = getItemAnimationStyle(automation.id, index);

                return (
                  <div key={automation.id} style={animStyle}>
                    <TimelineListitem
                      automation={automation}
                      timeRange={timeRange}
                      scheduledAutomations={scheduledAutomations}
                      isRunning={runningIds.has(automation.id)}
                      isStopping={stoppingIds.has(automation.id)}
                      onTimelineClick={handleTimelineClick}
                      onViewData={handleViewData}
                      onViewHistory={handleViewHistory}
                      onViewRunDetails={handleViewRunDetails}
                      onRunNow={handleRunNow}
                      onStop={handleStopAutomation}
                      onResume={handleOpenResumeModal}
                      onSettings={handleSettings}
                      onSchedule={(a) => setScheduleModal(a)}
                      onDelete={handleDeleteAutomation}
                      onExpandImage={setExpandedImage}
                      isSelected={selectedIds.has(automation.id)}
                      onToggleSelect={handleToggleSelect}
                      scheduleButtonRef={scheduleButtonRefs.current[automation.id]}
                    />
                  </div>
                );
              })}
            </div>

            {/* Tablet View */}
            <TabletView
              automations={filteredAutomations}
              scheduledAutomations={scheduledAutomations}
              runningIds={runningIds}
              stoppingIds={stoppingIds}
              selectedIds={selectedIds}
              timeRange={timeRange}
              onRunNow={handleRunNow}
              onStop={handleStopAutomation}
              onResume={handleOpenResumeModal}
              onSettings={handleSettings}
              onSchedule={(a) => setScheduleModal(a)}
              onDelete={handleDeleteAutomation}
              onViewHistory={handleViewHistory}
              onViewData={handleViewData}
              onViewRunDetails={handleViewRunDetails}
              onToggleSelect={handleToggleSelect}
              onTimelineClick={handleTimelineClick}
              getItemAnimationStyle={getItemAnimationStyle}
            />

            {/* Mobile View */}
            <MobileView
              automations={filteredAutomations}
              scheduledAutomations={scheduledAutomations}
              runningIds={runningIds}
              stoppingIds={stoppingIds}
              selectedIds={selectedIds}
              timeRange={timeRange}
              onRunNow={handleRunNow}
              onStop={handleStopAutomation}
              onResume={handleOpenResumeModal}
              onSettings={handleSettings}
              onSchedule={(a) => setScheduleModal(a)}
              onDelete={handleDeleteAutomation}
              onViewHistory={handleViewHistory}
              onViewData={handleViewData}
              onViewRunDetails={handleViewRunDetails}
              onToggleSelect={handleToggleSelect}
              onTimelineClick={handleTimelineClick}
              getItemAnimationStyle={getItemAnimationStyle}
            />
          </>
        )}

        {/* Infinite scroll trigger */}
        <div ref={loadMoreTriggerRef} className="h-2" />

        {/* Skeleton loading placeholder */}
        {skeletonVisible && (
          <div className="pt-3">
            <div className="hidden lg:block">
              <SkeletonTimelineItem delay={0} />
            </div>
            <div className="hidden md:block lg:hidden">
              <SkeletonTabletCard delay={0} />
            </div>
            <div className="md:hidden">
              <SkeletonMobileCard delay={0} />
            </div>
          </div>
        )}

        {/* End of list indicator */}
        {!hasMore && automations.length > 0 && totalTasks > 10 && (
          <div className="text-[11px] text-gray-400 dark:text-gray-600 py-6 text-center tracking-wide">
            All {totalTasks} automations loaded
          </div>
        )}

       </div>

        {/* Bulk Action Bar - shown when items are selected */}
        {selectedIds.size > 0 && (
          <div className="sticky bottom-4 mx-4 z-10 animate-slide-up">
            <div className="bg-white/95 dark:bg-gray-900/90 backdrop-blur-xl rounded-xl border border-gray-200 dark:border-white/[0.1] shadow-2xl shadow-black/20 dark:shadow-black/50 px-4 py-3 max-w-xl mx-auto ring-1 ring-inset ring-white/[0.05]">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSelectAll}
                    className={`p-1.5 rounded-lg transition-colors ${
                      selectedIds.size === filteredAutomations.length
                        ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                    title={selectedIds.size === filteredAutomations.length ? 'Deselect all' : 'Select all'}
                  >
                    {selectedIds.size === filteredAutomations.length ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    )}
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-900 dark:text-white">{selectedIds.size}</span> selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleClearSelection}
                    className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {scheduleModal && (
        <ScheduleModal
          automation={scheduleModal}
          onClose={() => setScheduleModal(null)}
          onSave={handleSaveSchedule}
          anchorRef={scheduleButtonRefs.current[scheduleModal.id]}
        />
      )}
      {settingsModal && (
        <AutomationEditModal
          automation={settingsModal.automation}
          initialCategory={settingsModal.initialCategory}
          onClose={() => setSettingsModal(null)}
          onSave={handleSaveSettings}
        />
      )}
      {historyModal && (
        <RunHistoryModal
          automation={historyModal}
          isOpen={!!historyModal}
          onClose={() => setHistoryModal(null)}
          onViewRunDetails={handleViewRunDetailsFromHistory}
          onViewRunData={handleViewRunDataFromHistory}
        />
      )}
      {runModal && (
        <RunDetailsModal
          automation={runModal.automation}
          runId={runModal.runId}
          planName={planName}
          onClose={closeRun}
          onEdit={(automation, initialCategory) => handleSettings(automation, initialCategory)}
          onNavigate={(direction) => {
            const currentIndex = runModal.automation.timeline.findIndex((r) => r.runId === runModal.runId);
            const newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

            if (newIndex >= 0 && newIndex < runModal.automation.timeline.length) {
              const newRun = runModal.automation.timeline[newIndex];
              if (newRun.runId) {
                // replace: stepping through runs shouldn't stack history entries — back closes the dialog.
                openRun(runModal.automation, newRun.runId, { replace: true });
              }
            }
          }}
        />
      )}
      {expandedImage && (
        <ImageGalleryModal
          screenshots={expandedImage.screenshots}
          title={expandedImage.title}
          onClose={() => setExpandedImage(null)}
        />
      )}
      {showBulkDeleteModal && (
        <DeleteConfirmModal
          count={selectedIds.size}
          runningCount={selectedRunningCount}
          onClose={() => setShowBulkDeleteModal(false)}
          onConfirm={handleBulkDelete}
        />
      )}
      {createModal && (
        <AutomationCreateModal
          onClose={() => setCreateModal(false)}
          onCreated={() => {
            setCreateModal(false);
            refresh();
          }}
          onNoCredits={() => setShowNoCreditsModal(true)}
          onMixedDomains={() => setShowMixedDomainsModal(true)}
        />
      )}
      {resumeModal && (
        <ResumeConfirmModal
          automation={resumeModal.automation}
          failedRunId={resumeModal.failedRunId}
          onClose={() => setResumeModal(null)}
          onConfirm={handleConfirmResume}
        />
      )}
      {showNoCreditsModal && (
        <NoCreditsModal onClose={() => setShowNoCreditsModal(false)} planName={planName} />
      )}
      {showMixedDomainsModal && (
        <MixedDomainsModal onClose={() => setShowMixedDomainsModal(false)} />
      )}

      {/* Custom Scrollbar Styles */}
      <ScrollbarStyles />
    </div>
  );
};

// === Helper Components ===

// ── MiniTimeline ─────────────────────────────────────────────────────
// Compact horizontal bar chart reusing desktop's time-bucketing logic
const MiniTimeline = ({ automation, timeRange, barCount = 24, height = 20, onBarClick }) => {
  const timeline = automation.timeline || [];

  const groupedRuns = useMemo(() => {
    let timeWindow;
    switch (timeRange) {
      case "hourly": timeWindow = 60 * 60 * 1000; break;
      case "weekly": timeWindow = 7 * 24 * 60 * 60 * 1000; break;
      case "monthly": timeWindow = 30 * 24 * 60 * 60 * 1000; break;
      default: timeWindow = 24 * 60 * 60 * 1000; break;
    }
    const now = Date.now();
    const slotDuration = timeWindow / barCount;
    const slots = [];
    for (let i = 0; i < barCount; i++) {
      const slotStart = now - timeWindow + i * slotDuration;
      const slotEnd = slotStart + slotDuration;
      const runsInSlot = timeline.filter(r => r.timestamp >= slotStart && r.timestamp < slotEnd);
      if (runsInSlot.length > 0) {
        const sorted = [...runsInSlot].sort((a, b) => b.timestamp - a.timestamp);
        const hasRunning = sorted.some(r => r.status === "running");
        const hasQueued = sorted.some(r => r.status === "queued");
        let displayStatus = sorted[0].status;
        if (hasRunning) displayStatus = "running";
        else if (hasQueued) displayStatus = "queued";
        slots.push({ ...sorted[0], status: displayStatus, count: runsInSlot.length, slotStart, slotEnd });
      } else {
        slots.push({ timestamp: slotStart, status: "empty", count: 0, slotStart, slotEnd });
      }
    }
    return slots;
  }, [timeline, timeRange, barCount]);

  const hasAnyRuns = groupedRuns.some(s => s.status !== "empty");

  const barColorClass = (status) => {
    switch (status) {
      case "success": return "bg-emerald-500/80 dark:bg-emerald-400/70";
      case "failed": return "bg-rose-500/80 dark:bg-rose-400/70";
      case "running": return "bg-amber-500/80 dark:bg-amber-400/70 animate-pulse";
      case "queued": return "bg-orange-500/80 dark:bg-orange-400/70";
      case "stopped": return "bg-gray-400/60 dark:bg-gray-500/50";
      default: return "bg-gray-200/40 dark:bg-gray-700/20";
    }
  };

  return (
    <div
      className="flex items-end gap-[1.5px] rounded-lg bg-gray-50/50 dark:bg-white/[0.01] px-2 py-1.5"
      style={{ height: `${height + 12}px` }}
    >
      {hasAnyRuns ? groupedRuns.map((slot, i) => (
        <div
          key={i}
          className={`flex-1 min-w-0 rounded-sm transition-all duration-150 ${barColorClass(slot.status)} ${
            slot.status !== "empty" ? "cursor-pointer hover:opacity-80" : ""
          }`}
          style={{ height: slot.status === "empty" ? "3px" : `${height * 0.85}px` }}
          onClick={() => slot.status !== "empty" && slot.runId && onBarClick?.(automation, slot)}
        />
      )) : (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[10px] text-gray-400 dark:text-gray-600">No run history</span>
        </div>
      )}
    </div>
  );
};

// ── StatusDisplay ────────────────────────────────────────────────────
// Compact status rendering reusing the 5-state logic from LeftColumn
const StatusDisplay = ({ automation }) => {
  const latestRun = automation.timeline?.[0];
  const isQueued = latestRun?.status === 'queued';
  const isRunning = latestRun?.status === 'running';
  const isActive = isQueued || isRunning;
  const extractionCount = latestRun?.extraction?.rowCount || 0;
  const finalCount = latestRun?.itemCount || extractionCount;
  const journalStep = latestRun?.last_journal_step;

  // State 1: Running with extraction in progress
  if (isRunning && extractionCount > 0) {
    return (
      <div className="flex items-center gap-1.5">
        <LiveDot color="emerald" />
        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">{extractionCount}</span>
        <span className="text-[11px] text-gray-500 dark:text-gray-500">rows</span>
      </div>
    );
  }

  // State 2: Active with journal step
  if (isActive && journalStep) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <LiveDot color="amber" />
        <span className="text-[11px] text-amber-600 dark:text-amber-400 truncate">{isQueued ? 'Queued' : 'Running'}</span>
      </div>
    );
  }

  // State 3: Active, no journal yet
  if (isActive) {
    return (
      <div className="flex items-center gap-1.5">
        <LiveDot color="amber" />
        <span className="text-[11px] text-amber-600 dark:text-amber-400">{isQueued ? 'In queue' : 'Starting'}</span>
      </div>
    );
  }

  // State 4: Completed with results
  if (finalCount > 0) {
    return (
      <span className="text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">{finalCount}</span> rows
      </span>
    );
  }

  // State 5: No data
  return <span className="text-[11px] text-gray-400 dark:text-gray-500">No runs yet</span>;
};

// ── ActionMenu ───────────────────────────────────────────────────────
// Touch-friendly dropdown for secondary actions
const ActionMenu = ({ automation, scheduledAutomations, isRunning, onSettings, onSchedule, onResume, onDelete, onViewHistory, onViewData }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [open]);

  const latestRun = automation.timeline?.[0];
  const isTerminal = ['failed', 'stopped', 'cancelled'].includes(latestRun?.status);
  const isResumable = isTerminal && latestRun?.hasCheckpoint && !latestRun?.checkpointArchived && !isRunning;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-colors"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-2xl shadow-black/20 dark:shadow-black/50 py-1 ring-1 ring-inset ring-white/[0.05]"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          {/* History (mobile only - tablet has inline button) */}
          <button
            onClick={() => { setOpen(false); onViewHistory(automation); }}
            className="md:hidden w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
          >
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            History
          </button>
          {/* Data (mobile only) */}
          {automation.latestTableId && (
            <button
              onClick={() => { setOpen(false); onViewData(automation); }}
              className="md:hidden w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12c-.621 0-1.125.504-1.125 1.125M12 10.875c-.621 0-1.125.504-1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 1.5c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-2.25 0c-.621 0-1.125.504-1.125 1.125m0 1.5v-1.5" />
              </svg>
              View Data
            </button>
          )}
          {/* Mobile-only divider */}
          <div className="md:hidden h-px bg-gray-100 dark:bg-white/[0.06] my-1" />
          {/* Settings */}
          <button
            onClick={() => { setOpen(false); onSettings(automation); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
          >
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>
          {/* Schedule */}
          <button
            onClick={() => { setOpen(false); onSchedule(automation); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
          >
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Schedule
            {scheduledAutomations?.has(automation.id) && (
              <span className="ml-auto text-[10px] font-medium text-emerald-500 dark:text-emerald-400">Active</span>
            )}
          </button>
          {/* Open Website */}
          {automation.website && (
            <button
              onClick={() => { setOpen(false); window.open(automation.website.startsWith('http') ? automation.website : `https://${automation.website}`, '_blank'); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              Open Website
            </button>
          )}
          {/* Resume */}
          {isResumable && (
            <button
              onClick={() => { setOpen(false); onResume(automation, latestRun); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
              Resume
            </button>
          )}
          {/* Divider */}
          <div className="h-px bg-gray-100 dark:bg-white/[0.06] my-1" />
          {/* Delete */}
          <button
            onClick={() => { setOpen(false); onDelete(automation.id); }}
            disabled={isRunning}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs transition-colors ${
              isRunning
                ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {isRunning ? "Stop first" : "Delete"}
          </button>
        </div>
      )}
    </div>
  );
};

// ── RunStopButton ────────────────────────────────────────────────────
// Context-aware primary action button (compact icon or icon+label)
const RunStopButton = ({ automation, runningIds, stoppingIds, onRunNow, onStop, variant = "icon" }) => {
  const hasActiveRun = automation.timeline?.some(r => r.status === 'running' || r.status === 'queued');
  const isRunning = runningIds?.has(automation.id) || hasActiveRun;
  const isStopping = stoppingIds?.has(automation.id);
  const canStop = hasActiveRun && !isStopping;

  const handleClick = () => {
    if (canStop) onStop(automation);
    else if (!isRunning && !isStopping) onRunNow(automation.id);
  };

  const btnClass = isStopping
    ? "bg-red-50 dark:bg-red-950/20 text-red-500 dark:text-red-400 border-red-200/60 dark:border-red-500/20 cursor-not-allowed"
    : canStop
    ? "bg-red-50 dark:bg-red-950/20 text-red-500 dark:text-red-400 border-red-200/60 dark:border-red-500/20 hover:bg-red-100 dark:hover:bg-red-950/30"
    : isRunning
    ? "bg-amber-50 dark:bg-amber-950/20 text-amber-500 dark:text-amber-400 border-amber-200/60 dark:border-amber-500/20 cursor-not-allowed"
    : "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-500 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-950/30";

  if (variant === "label") {
    const label = isStopping ? "Stopping" : canStop ? "Stop" : isRunning ? "Running" : "Run";
    return (
      <button
        onClick={handleClick}
        disabled={isStopping || (isRunning && !canStop)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 ${btnClass}`}
      >
        {isStopping ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : canStop ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="1" /></svg>
        ) : isRunning ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
        )}
        {label}
      </button>
    );
  }

  // Icon-only variant
  return (
    <button
      onClick={handleClick}
      disabled={isStopping || (isRunning && !canStop)}
      className={`p-2 rounded-lg border transition-all duration-200 ${btnClass}`}
    >
      {isStopping ? (
        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ) : canStop ? (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="1" /></svg>
      ) : isRunning ? (
        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
      )}
    </button>
  );
};

// ── ScheduleBadge ────────────────────────────────────────────────────
const ScheduleBadge = ({ isScheduled }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${
    isScheduled
      ? "text-emerald-500 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
      : "text-gray-400 dark:text-gray-500 bg-gray-500/5 border-gray-500/10"
  }`}>
    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
    {isScheduled ? "Scheduled" : "Off"}
  </span>
);

// ── FaviconImg ───────────────────────────────────────────────────────
const FaviconImg = ({ src, className = "w-5 h-5" }) => {
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      className={`${className} rounded-md border border-gray-200/60 dark:border-white/[0.08] flex-shrink-0`}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
};

// ── TabletView (md to lg) ────────────────────────────────────────────
const TabletView = ({
  automations, scheduledAutomations, runningIds, stoppingIds, selectedIds, timeRange,
  onRunNow, onStop, onResume, onSettings, onSchedule, onDelete,
  onViewHistory, onViewData, onToggleSelect, onTimelineClick, getItemAnimationStyle
}) => (
  <div className="hidden md:block lg:hidden space-y-3">
    {automations.map((automation, index) => {
      const hasActiveRun = automation.timeline?.some(r => r.status === 'running' || r.status === 'queued');
      const isActive = hasActiveRun || runningIds.has(automation.id);
      const animStyle = getItemAnimationStyle?.(automation.id, index);
      return (
        <div
          key={automation.id}
          style={animStyle}
          className={`bg-white dark:bg-white/[0.02] border rounded-2xl overflow-hidden shadow-sm hover:shadow-card-hover dark:hover:shadow-card-hover-dark transition-all duration-300 ${
            isActive
              ? "border-amber-300/40 dark:border-amber-400/15"
              : "border-gray-200/60 dark:border-white/[0.06] hover:border-gray-300/60 dark:hover:border-white/[0.1]"
          }`}
        >
          {/* Header row */}
          <div className="flex items-center gap-3 px-3.5 pt-3 pb-2">
            {/* Selection checkbox */}
            {selectedIds.size > 0 && (
              <button
                onClick={() => onToggleSelect(automation.id)}
                className={`p-1 rounded-md transition-colors flex-shrink-0 ${
                  selectedIds.has(automation.id)
                    ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
                }`}
              >
                {selectedIds.has(automation.id) ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="3" strokeWidth={1.5} /></svg>
                )}
              </button>
            )}

            {/* Favicon + Name */}
            <FaviconImg src={automation.favicon} className="w-6 h-6" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-800 dark:text-gray-100 font-medium truncate">{automation.name}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-500 truncate">{automation.website}</div>
            </div>

            {/* Status indicator */}
            <div className="flex-shrink-0 hidden sm:block">
              <StatusDisplay automation={automation} />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <RunStopButton automation={automation} runningIds={runningIds} stoppingIds={stoppingIds} onRunNow={onRunNow} onStop={onStop} variant="label" />
              <button
                onClick={() => onViewData(automation)}
                disabled={!automation.latestTableId}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 ${
                  automation.latestTableId
                    ? "bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 border-blue-200/60 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-950/30"
                    : "bg-gray-50 dark:bg-gray-800/30 text-gray-400 dark:text-gray-600 border-gray-200/40 dark:border-gray-700/20 cursor-not-allowed"
                }`}
              >
                Data
              </button>
              <button
                onClick={() => onViewHistory(automation)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-gray-50 dark:bg-white/[0.03] text-gray-600 dark:text-gray-400 border-gray-200/60 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-800 dark:hover:text-gray-300 transition-all duration-200"
              >
                History
              </button>
              <ActionMenu
                automation={automation}
                scheduledAutomations={scheduledAutomations}
                isRunning={isActive}
                onSettings={onSettings}
                onSchedule={onSchedule}
                onResume={onResume}
                onDelete={onDelete}
                onViewHistory={onViewHistory}
                onViewData={onViewData}
              />
            </div>
          </div>

          {/* Mini Timeline */}
          <div className="px-3.5 pb-3">
            <MiniTimeline
              automation={automation}
              timeRange={timeRange}
              barCount={40}
              height={24}
              onBarClick={onTimelineClick}
            />
          </div>
        </div>
      );
    })}
  </div>
);

// ── MobileView (< md) ───────────────────────────────────────────────
const MobileView = ({
  automations, scheduledAutomations, runningIds, stoppingIds, selectedIds, timeRange,
  onRunNow, onStop, onResume, onSettings, onSchedule, onDelete,
  onViewHistory, onViewData, onToggleSelect, onTimelineClick, getItemAnimationStyle
}) => (
  <div className="md:hidden space-y-3">
    {automations.map((automation, index) => {
      const hasActiveRun = automation.timeline?.some(r => r.status === 'running' || r.status === 'queued');
      const isActive = hasActiveRun || runningIds.has(automation.id);
      const latestRun = automation.timeline?.[0];
      const animStyle = getItemAnimationStyle?.(automation.id, index);
      return (
        <div
          key={automation.id}
          style={animStyle}
          className={`bg-white dark:bg-white/[0.02] border rounded-2xl overflow-hidden shadow-sm transition-all duration-300 ${
            isActive
              ? "border-amber-300/40 dark:border-amber-400/15"
              : "border-gray-200/60 dark:border-white/[0.06]"
          }`}
        >
          {/* Header: Favicon + Name + Actions */}
          <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2">
            {/* Selection checkbox */}
            {selectedIds.size > 0 && (
              <button
                onClick={() => onToggleSelect(automation.id)}
                className={`p-1 rounded-md transition-colors flex-shrink-0 ${
                  selectedIds.has(automation.id)
                    ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
                }`}
              >
                {selectedIds.has(automation.id) ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="3" strokeWidth={1.5} /></svg>
                )}
              </button>
            )}

            <FaviconImg src={automation.favicon} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-800 dark:text-gray-100 font-medium truncate">{automation.name}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-500 truncate">{automation.website}</div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <RunStopButton automation={automation} runningIds={runningIds} stoppingIds={stoppingIds} onRunNow={onRunNow} onStop={onStop} variant="icon" />
              <ActionMenu
                automation={automation}
                scheduledAutomations={scheduledAutomations}
                isRunning={isActive}
                onSettings={onSettings}
                onSchedule={onSchedule}
                onResume={onResume}
                onDelete={onDelete}
                onViewHistory={onViewHistory}
                onViewData={onViewData}
              />
            </div>
          </div>

          {/* Mini Timeline */}
          <div className="px-3.5">
            <MiniTimeline
              automation={automation}
              timeRange={timeRange}
              barCount={24}
              height={20}
              onBarClick={onTimelineClick}
            />
          </div>

          {/* Status + Stats row */}
          <div className="flex items-center justify-between gap-3 px-3.5 pt-2 pb-3">
            <StatusDisplay automation={automation} />
            <div className="flex items-center gap-2 flex-shrink-0">
              {latestRun?.timestamp && (
                <span className="text-[10px] text-gray-400 dark:text-gray-600">
                  {formatLastRun(latestRun.timestamp)}
                </span>
              )}
              <ScheduleBadge isScheduled={scheduledAutomations.has(automation.id)} />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// ============================================
// Skeleton Loading Components (Facebook-style)
// ============================================

const ShimmerOverlay = () => (
  <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/50 dark:via-white/[0.06] to-transparent pointer-events-none" />
);

const SkeletonTimelineItem = ({ delay = 0 }) => (
  <div
    className="flex gap-2 lg:gap-2.5 xl:gap-3 relative min-h-[100px] lg:min-h-[115px]"
    style={{ animation: `fadeIn 0.5s ease-out ${delay}ms both` }}
  >
    {/* Left: Info card skeleton */}
    <div className="w-[220px] lg:w-[240px] xl:w-[260px] flex-shrink-0">
      <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-2.5 lg:p-3 h-full relative overflow-hidden">
        <div className="h-full flex flex-col justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gray-200/80 dark:bg-gray-700/40 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-gray-200/80 dark:bg-gray-700/40 rounded-md w-[72%] animate-pulse" />
              <div className="h-2.5 bg-gray-200/50 dark:bg-gray-700/25 rounded-md w-[48%] animate-pulse" style={{ animationDelay: '150ms' }} />
            </div>
          </div>
          <div className="mt-3">
            <div className="h-2.5 bg-gray-200/50 dark:bg-gray-700/25 rounded-md w-[58%] animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
        <ShimmerOverlay />
      </div>
    </div>

    {/* Center: Timeline skeleton */}
    <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-2.5 lg:p-3 flex-1 min-w-0 relative overflow-hidden">
      <div className="flex flex-col h-full">
        {/* Header: action button placeholders */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-7 w-16 bg-gray-200/60 dark:bg-gray-700/30 rounded-lg animate-pulse" />
            <div className="h-7 w-20 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '100ms' }} />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-7 w-7 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '150ms' }} />
            <div className="h-7 w-7 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '200ms' }} />
            <div className="h-7 w-7 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '250ms' }} />
          </div>
        </div>
        {/* Timeline bars */}
        <div className="flex-1 flex items-end gap-[2px]">
          {Array.from({ length: 96 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 min-w-0 bg-gray-200/40 dark:bg-gray-700/20 rounded-t-sm animate-pulse"
              style={{
                height: `${26 + Math.sin(i * 0.7) * 3}px`,
                animationDelay: `${(i % 8) * 120}ms`,
              }}
            />
          ))}
        </div>
      </div>
      <ShimmerOverlay />
    </div>

    {/* Right: Image preview skeleton (xl+ only) */}
    <div className="hidden md:block w-[160px] 2xl:w-[180px] flex-shrink-0">
      <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl h-full relative overflow-hidden flex items-center justify-center">
        <svg className="w-8 h-8 text-gray-200 dark:text-gray-700/40 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
        </svg>
        <ShimmerOverlay />
      </div>
    </div>
  </div>
);

// Tablet skeleton - matches new tablet card layout
const SkeletonTabletCard = ({ delay = 0 }) => (
  <div
    className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl overflow-hidden relative"
    style={{ animation: `fadeIn 0.5s ease-out ${delay}ms both` }}
  >
    <div className="flex items-center gap-3 px-3.5 pt-3 pb-2">
      <div className="w-6 h-6 rounded-md bg-gray-200/80 dark:bg-gray-700/40 animate-pulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200/80 dark:bg-gray-700/40 rounded-md w-[45%] animate-pulse" />
        <div className="h-2.5 bg-gray-200/50 dark:bg-gray-700/25 rounded-md w-[30%] animate-pulse" style={{ animationDelay: '150ms' }} />
      </div>
      <div className="flex items-center gap-1.5">
        <div className="h-8 w-16 bg-gray-200/50 dark:bg-gray-700/25 rounded-lg animate-pulse" style={{ animationDelay: '200ms' }} />
        <div className="h-8 w-12 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '250ms' }} />
        <div className="h-8 w-14 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
    <div className="px-3.5 pb-3">
      <div className="flex items-end gap-[1.5px] rounded-lg bg-gray-50/50 dark:bg-white/[0.01] px-2 py-1.5 h-9">
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={i} className="flex-1 min-w-0 bg-gray-200/40 dark:bg-gray-700/20 rounded-sm animate-pulse" style={{ height: '16px', animationDelay: `${(i % 6) * 100}ms` }} />
        ))}
      </div>
    </div>
    <ShimmerOverlay />
  </div>
);

// Mobile skeleton - matches new mobile card layout
const SkeletonMobileCard = ({ delay = 0 }) => (
  <div
    className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl overflow-hidden relative"
    style={{ animation: `fadeIn 0.5s ease-out ${delay}ms both` }}
  >
    <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2">
      <div className="w-5 h-5 rounded-md bg-gray-200/80 dark:bg-gray-700/40 animate-pulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200/80 dark:bg-gray-700/40 rounded-md w-[55%] animate-pulse" />
        <div className="h-2.5 bg-gray-200/50 dark:bg-gray-700/25 rounded-md w-[35%] animate-pulse" style={{ animationDelay: '150ms' }} />
      </div>
      <div className="flex items-center gap-1">
        <div className="w-8 h-8 bg-gray-200/50 dark:bg-gray-700/25 rounded-lg animate-pulse" style={{ animationDelay: '200ms' }} />
        <div className="w-8 h-8 bg-gray-200/40 dark:bg-gray-700/20 rounded-lg animate-pulse" style={{ animationDelay: '250ms' }} />
      </div>
    </div>
    <div className="px-3.5">
      <div className="flex items-end gap-[1.5px] rounded-lg bg-gray-50/50 dark:bg-white/[0.01] px-2 py-1.5 h-8">
        {Array.from({ length: 24 }).map((_, i) => (
          <div key={i} className="flex-1 min-w-0 bg-gray-200/40 dark:bg-gray-700/20 rounded-sm animate-pulse" style={{ height: '14px', animationDelay: `${(i % 5) * 100}ms` }} />
        ))}
      </div>
    </div>
    <div className="flex items-center justify-between px-3.5 pt-2 pb-3">
      <div className="h-3 bg-gray-200/50 dark:bg-gray-700/25 rounded-md w-[30%] animate-pulse" style={{ animationDelay: '300ms' }} />
      <div className="h-4 w-16 bg-gray-200/40 dark:bg-gray-700/20 rounded-md animate-pulse" style={{ animationDelay: '350ms' }} />
    </div>
    <ShimmerOverlay />
  </div>
);

const ScrollbarStyles = () => (
  <style
    dangerouslySetInnerHTML={{
      __html: `
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(209, 213, 219, 0.8);
        border-radius: 3px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(156, 163, 175, 0.8);
      }
      .dark .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(75, 85, 99, 0.4);
      }
      .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(75, 85, 99, 0.6);
      }
      `,
    }}
  />
);

export default AutomationsView;
